import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { fetchPricingContext, type AppliedProfile } from "@/lib/api";
import { summarizePricebook } from "@/lib/pricingForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const STATUS_VARIANT: Record<AppliedProfile["status"], "success" | "warning" | "neutral"> = {
  active: "success", scheduled: "warning", expired: "neutral",
};

function validPeriod(p: AppliedProfile): string {
  if (!p.valid_from_utc && !p.valid_until_utc) return "Always";
  const f = p.valid_from_utc ? new Date(p.valid_from_utc).toLocaleDateString() : "—";
  const u = p.valid_until_utc ? new Date(p.valid_until_utc).toLocaleDateString() : "—";
  return `${f} → ${u}`;
}

/**
 * Charger-details "Applied Pricing Profiles": every pricing profile that can affect
 * charging on this charger (direct-unit + charger-group + site-wide scopes), with the
 * SAME applicability + precedence the production resolver uses (fetched from the backend
 * pricing-context endpoint — never recomputed here). Uses the shared summarizePricebook
 * so idle/activation are never omitted. Manager/admin-only (the endpoint enforces it).
 */
export function AppliedPricingProfiles({ unitId }: { unitId: string }) {
  const q = useQuery({
    queryKey: ["pricing-context", unitId],
    queryFn: () => fetchPricingContext(unitId),
    enabled: !!unitId,
    retry: false,
  });

  // A driver / unrelated manager gets 403/404 — silently render nothing (no leak).
  if (q.isError) return null;
  if (q.isLoading) return <div className="text-sm opacity-60">Loading pricing profiles…</div>;

  const ctx = q.data!;
  const profiles = ctx.applied_profiles ?? [];
  // Group by lifecycle status so an EXPIRED profile is never presented as currently
  // "applied". The backend already sorts each input by precedence (most specific first);
  // partitioning preserves that order within each group. Active is shown first and is
  // visually distinct; expired is collapsed into a history disclosure.
  const active = profiles.filter((p) => p.status === "active");
  const scheduled = profiles.filter((p) => p.status === "scheduled");
  const expired = profiles.filter((p) => p.status === "expired");

  return (
    <div className="border-t pt-4 space-y-3" data-testid="applied-pricing-profiles">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold">Applied Pricing Profiles</h3>
        {ctx.organization_id != null && (
          <Button asChild size="xs" variant="outline">
            <Link to="/pricing-profiles" data-testid="manage-pricing-profiles">Manage Pricing Profiles</Link>
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        {ctx.precedence_explanation} These profiles apply only to eligible drivers — other
        drivers, and guests, use the default rate above.
      </p>

      {profiles.length === 0 ? (
        <div className="text-sm text-muted-foreground" data-testid="applied-profiles-empty">
          No pricing profiles apply to this charger. All drivers use the default rate.
        </div>
      ) : (
        <div className="space-y-4">
          {/* Active — currently applied, shown first and prominently. */}
          <section className="space-y-2" data-testid="applied-active">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Active profiles
            </h4>
            {active.length === 0 ? (
              <div className="text-sm text-muted-foreground" data-testid="applied-active-empty">
                No profiles are active right now. All drivers currently use the default rate.
              </div>
            ) : (
              active.map((p) => <ProfileCard key={p.assignment_id} p={p} />)
            )}
          </section>

          {scheduled.length > 0 && (
            <section className="space-y-2" data-testid="applied-scheduled">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Scheduled profiles
              </h4>
              <p className="text-xs text-muted-foreground">Not yet active — they begin on their start date.</p>
              {scheduled.map((p) => <ProfileCard key={p.assignment_id} p={p} />)}
            </section>
          )}

          {expired.length > 0 && (
            <details className="text-sm" data-testid="applied-expired">
              <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Expired profiles ({expired.length}) — history
              </summary>
              <div className="mt-2 space-y-2 opacity-70">
                {expired.map((p) => <ProfileCard key={p.assignment_id} p={p} />)}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function ProfileCard({ p }: { p: AppliedProfile }) {
  return (
    <div className="rounded-md border p-3 text-sm" data-testid={`applied-profile-${p.profile_id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{p.profile_name}</span>
        <Badge variant={STATUS_VARIANT[p.status]} className="capitalize">{p.status}</Badge>
      </div>
      <div className="mt-1" data-testid={`applied-summary-${p.profile_id}`}>
        {summarizePricebook(p.pricebook)}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        Audience:{" "}
        {p.audience_type === "driver_group" ? `${p.audience_name} driver group`
          : p.audience_type === "user" ? `${p.audience_name} (individual driver)`
          : "Any signed-in driver"}
        {" · Scope: "}{p.scope}
        {" · "}{validPeriod(p)}
      </div>
    </div>
  );
}
